import { useEffect } from 'react';
import { generatePath, useLoaderData, useLocation, useNavigate } from 'react-router';
import { paths } from '../../../../../common/constants/pathVariables.js';
import { devicesMsg, omaElisaForCompaniesMsg, redemptionMsg, t } from '../../../../../common/i18n';
import { getSubscriptionBreadcrumbName, getSubscriptionUrlId } from '../../../../../common/utils/subscriptionUtils.js';
import { BreadCrumbsWithTitle } from '../../../../../components/BreadCrumbsWithTitle/BreadCrumbsWithTitle.js';
import { DetailsWrapper } from '../../../../../components/DetailsWrapper';
import { isEppDeviceSubscription } from '../../../../../components/EoeSubscriptionDetails/eppSubscriptionUtils.js';
import { EppRedeemAdmin } from '../../../../../components/EppRedeem/EppRedeemAdmin.js';
import type { DeviceSubPostChangeRequestLoaderResponse } from '../../../../../common/loaders.js';

const generateDeviceDetailsPath = (subscriptionId: string, search: string) =>
  generatePath(paths.PS_DEVICE, { subscriptionId: subscriptionId }) + search;

export const DeviceRedemptionPath = () => {
  const navigate = useNavigate();
  const { companyInfo, hasPendingActions, subscription } = useLoaderData() as DeviceSubPostChangeRequestLoaderResponse;
  const { search } = useLocation();
  const companyName = companyInfo.companyName;
  const subscriptionId = subscription?.subscriptionId;
  const isRedeemableEppSubscription = isEppDeviceSubscription(subscription) && !hasPendingActions;

  useEffect(() => {
    if (!isRedeemableEppSubscription) {
      navigate(generateDeviceDetailsPath(getSubscriptionUrlId(subscription), search), {
        replace: true,
      });
    }
  }, [isRedeemableEppSubscription, navigate, search, subscription]);

  if (!isRedeemableEppSubscription) {
    return null;
  }

  return (
    <DetailsWrapper
      classes={['of-subscription-details']}
      detailsTop={
        <BreadCrumbsWithTitle
          breadCrumbPaths={[
            { name: t.VCUZ(omaElisaForCompaniesMsg), path: paths.SELF_SERVICE_HOME },
            { name: t.JJTM(devicesMsg), path: paths.PS_DEVICES },
            {
              name: getSubscriptionBreadcrumbName(subscription),
              path: generateDeviceDetailsPath(getSubscriptionUrlId(subscription), search),
            },
            { name: t.B9EG(redemptionMsg) },
          ]}
        />
      }
      heading={t.B9EG('Redeeming your device')}
      headingBottom={
        `${subscription.subscriptionName}` +
        (subscription?.subscriptionContactName ? ` | ${subscription.subscriptionContactName}` : '')
      }
      heroPicto={undefined}
      heroIcon="invoice"
      id={`subscription-details-${subscriptionId}`}
    >
      <EppRedeemAdmin
        companyName={companyName}
        onBackClick={() => navigate(generateDeviceDetailsPath(getSubscriptionUrlId(subscription), search))}
        subscription={subscription}
      />
    </DetailsWrapper>
  );
};
